import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { errorMessage, get, post } from "../api";
import { Alert, Button, Field } from "../components/ui";
import { useSession } from "../session";

const MESSAGES: Record<string, string> = {
  no_account: "There's no user for that Google account. Ask an admin to add you.",
  deactivated: "That user has been deactivated. Ask an admin if that's a mistake.",
  expired: "Sign-in took too long. Try again.",
  cancelled: "Sign-in was cancelled.",
};

/** Google sign-in. Locally (dev mode) there's an email-only sign-in instead. */
export function SignIn() {
  const { signedOutReason, refresh } = useSession();
  const mode = useQuery({ queryKey: ["auth-mode"], queryFn: () => get<{ mode: "google" | "dev" }>("/api/auth/mode") });
  const params = new URLSearchParams(window.location.search);
  const errorCode = params.get("signin_error");
  const error = errorCode ? (params.get("message") ?? MESSAGES[errorCode] ?? "Sign-in didn't work. Try again.") : null;
  const returnTo = window.location.pathname === "/" ? "/" : window.location.pathname + window.location.search;
  // The logo uploaded in Settings; the house and name if there isn't one.
  const [logo, setLogo] = useState(true);

  return (
    <div className="grid min-h-dvh place-items-center bg-paper px-4">
      <div className="w-full max-w-sm">
        <div className="flex justify-center rounded-t-[var(--radius-panel)] border-b border-mist bg-white px-6 py-6">
          {logo ? (
            <img src="/api/public/logo" alt="NMT Construction" className="max-h-20 max-w-full object-contain" onError={() => setLogo(false)} />
          ) : (
            <div className="flex items-center gap-3">
              <img src="/icon-192.png" alt="" className="size-10 object-contain" />
              <div className="text-2xl font-bold">NMT Construction</div>
            </div>
          )}
        </div>
        <div className="rounded-b-[var(--radius-panel)] bg-white px-6 py-6 shadow-[var(--shadow-card)]">
          <h1 className="text-lg font-semibold">Sign in</h1>
          <div className="mt-4 flex flex-col gap-3">
            {signedOutReason && <Alert tone="info">{signedOutReason}</Alert>}
            {error && <Alert>{error}</Alert>}
            {mode.data?.mode === "google" && (
              <a
                href={`/api/auth/google/start?returnTo=${encodeURIComponent(returnTo)}`}
                className="flex items-center justify-center gap-3 rounded-[var(--radius-control)] border border-line bg-white px-4 py-2.5 font-semibold hover:border-ink-soft"
              >
                <GoogleMark />
                Sign in with Google
              </a>
            )}
            {mode.data?.mode === "dev" && <DevSignIn onDone={refresh} />}
          </div>
          <p className="mt-5 text-sm text-muted">Use your NMT Google Workspace account. Two-step verification is handled by Google.</p>
        </div>
      </div>
    </div>
  );
}

function DevSignIn({ onDone }: { onDone: () => Promise<void> }) {
  const [email, setEmail] = useState("ryan@nmtconstruction.com");
  const signIn = useMutation({ mutationFn: () => post("/api/auth/dev-login", { email }), onSuccess: onDone });
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        signIn.mutate();
      }}
    >
      <Alert tone="info">Development sign-in: no password, local only.</Alert>
      {signIn.error && <Alert>{errorMessage(signIn.error)}</Alert>}
      <Field label="Email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} autoFocus />
      <Button variant="primary" type="submit" busy={signIn.isPending}>
        Sign in
      </Button>
    </form>
  );
}

function GoogleMark() {
  return (
    <svg viewBox="0 0 48 48" className="size-5" aria-hidden>
      <path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z" />
      <path fill="#FF3D00" d="m6.3 14.7 6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z" />
      <path fill="#4CAF50" d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-7.9l-6.5 5C9.5 39.6 16.2 44 24 44z" />
      <path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z" />
    </svg>
  );
}
